import type {Position} from '@github/turbo/dist/types/core/types'
import {noop} from '@github-ui/noop'
import {ssrSafeLocation, ssrSafeWindow} from '@github-ui/ssr-utils'
import {useLayoutEffect} from 'react'

const scrollMap = new Map<string, Position>()

let installed = false
let previousHref = ssrSafeLocation.href

async function saveScrollPosition() {
  const {session} = await import('@github/turbo')

  document.addEventListener('turbo:click', event => {
    previousHref = event.detail.url
  })

  window.addEventListener('popstate', () => {
    const {scrollPosition} =
      session.history.getRestorationDataForIdentifier(session.history.restorationIdentifier) || {}
    if (!scrollPosition) return
    scrollMap.set(window.location.href, scrollPosition)
  })
}

export async function installScrollRestoration() {
  if (ssrSafeWindow) {
    if (installed) return
    await saveScrollPosition()
    installed = true
  }
}

function useScrollRestorationInBrowser() {
  useLayoutEffect(() => {
    const href = window.location.href

    // When clicking on the same hash link, don't restore scroll
    if (href === previousHref && href.includes('#')) {
      previousHref = href
      return
    }

    // Skip restoration for hash-only navigations (same pathname, different/new hash).
    // This prevents interfering with virtualized scroll-to-line behavior.
    if (isHashOnlyChange(previousHref, href)) {
      previousHref = href
      return
    }

    previousHref = href

    const scroll = scrollMap.get(href)

    if (!scroll) return
    const timeout = requestAnimationFrame(() => {
      window.scrollTo(scroll.x, scroll.y)
    })
    return () => {
      cancelAnimationFrame(timeout)
    }
  })
}

export function clear() {
  scrollMap.clear()
  installed = false
}
/**
 * This hook restores turbo-scroll-restoration position AFTER the page has been rendered.
 * Otherwise, turbo was restoring scroll on the page before react had rendered.
 */
export const useScrollRestoration = ssrSafeWindow ? useScrollRestorationInBrowser : noop

/**
 * Checks if the navigation is a hash-to-hash change (same path and search, both have hashes).
 * This is used to skip scroll restoration when navigating between line anchors on the same page,
 * which allows the virtualized code view to scroll to the correct line without interference.
 * We only skip when both URLs have hashes to allow restoration when navigating from a hashless URL.
 */
function isHashOnlyChange(prevHref: string, newHref: string): boolean {
  try {
    const prevUrl = new URL(prevHref, window.location.origin)
    const newUrl = new URL(newHref, window.location.origin)

    // Only skip for hash-to-hash navigation (both have hashes and they're different)
    return (
      prevUrl.pathname === newUrl.pathname &&
      prevUrl.search === newUrl.search &&
      prevUrl.hash !== '' &&
      newUrl.hash !== '' &&
      prevUrl.hash !== newUrl.hash
    )
  } catch {
    return false
  }
}
